/**
 * In-memory cache with TTL (Time To Live) support for TEFAS API responses.
 *
 * This cache helps reduce redundant API calls by storing responses temporarily.
 * Each cache entry expires after the configured TTL period.
 */

/**
 * Configuration options for the cache
 *
 * @public
 */
export interface CacheOptions {
  /**
   * Time to live in milliseconds. Default: 15 minutes (900000ms)
   */
  ttl?: number;

  /**
   * Whether caching is enabled. Default: true
   */
  enabled?: boolean;

  /**
   * Maximum number of cache entries. Default: 1000
   * When exceeded, oldest entries are removed (LRU-style)
   */
  maxSize?: number;
}

/**
 * Cache entry with expiration timestamp
 */
interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  createdAt: number;
}

/**
 * In-memory TTL-based cache implementation
 */
export class TtlCache<T> {
  private cache = new Map<string, CacheEntry<T>>();
  private readonly ttl: number;
  private readonly enabled: boolean;
  private readonly maxSize: number;

  /**
   * Creates a new TTL cache instance
   *
   * @param options - Cache configuration options
   */
  constructor(options: CacheOptions = {}) {
    this.ttl = options.ttl ?? 15 * 60 * 1000; // Default: 15 minutes
    this.enabled = options.enabled ?? true;
    this.maxSize = options.maxSize ?? 1000;
  }

  /**
   * Gets a value from the cache
   *
   * @param key - Cache key
   * @returns Cached value or undefined if not found or expired
   */
  get(key: string): T | undefined {
    if (!this.enabled) {
      return undefined;
    }

    const entry = this.cache.get(key);

    if (!entry) {
      return undefined;
    }

    const now = Date.now();

    // Check if entry has expired
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.value;
  }

  /**
   * Sets a value in the cache
   *
   * @param key - Cache key
   * @param value - Value to cache
   */
  set(key: string, value: T): void {
    if (!this.enabled) {
      return;
    }

    // Enforce max size by removing oldest entries (LRU-style)
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    const now = Date.now();
    const entry: CacheEntry<T> = {
      value,
      expiresAt: now + this.ttl,
      createdAt: now,
    };

    this.cache.set(key, entry);
  }

  /**
   * Checks if a key exists in the cache and is not expired
   *
   * @param key - Cache key
   * @returns true if key exists and is valid
   */
  has(key: string): boolean {
    if (!this.enabled) {
      return false;
    }

    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    const now = Date.now();

    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Deletes a specific key from the cache
   *
   * @param key - Cache key to delete
   * @returns true if key was deleted, false if it didn't exist
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clears all entries from the cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Gets the current number of entries in the cache
   *
   * @returns Number of cache entries
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Removes all expired entries from the cache
   *
   * @returns Number of entries removed
   */
  prune(): number {
    if (!this.enabled) {
      return 0;
    }

    const now = Date.now();
    let removed = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        removed++;
      }
    }

    return removed;
  }

  /**
   * Evicts the oldest entry from the cache (by creation time)
   * Used to enforce max size limit
   */
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.createdAt < oldestTime) {
        oldestTime = entry.createdAt;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Gets cache statistics
   *
   * @returns Cache statistics object
   */
  getStats(): { size: number; enabled: boolean; ttl: number; maxSize: number } {
    return {
      size: this.size,
      enabled: this.enabled,
      ttl: this.ttl,
      maxSize: this.maxSize,
    };
  }
}
